import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useUser } from "@/lib/user";
import { api, HttpError } from "@/lib/api";
import { ICON_MAP, questById } from "@/lib/quests";
import { Button } from "@/components/ui/button";
import { TypingDots } from "@/components/TypingDots";
import { ArrowRight, X, Send } from "lucide-react";

const PERSONA_REGEX = /You are (.+?)!/;
const TARGET_USER_MSGS = 6;

export default function QuestPage({ params }) {
  const questId = Number(params.id);
  const quest = questById(questId);
  const [, navigate] = useLocation();
  const { user, patchUser } = useUser();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);
  // Tracks the questId whose messages are currently loaded. When the URL param
  // switches to a different quest (same-component route change), this drifts
  // out of sync with `questId` and we re-init below.
  const initedForRef = useRef(null);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    if (!quest) {
      navigate("/dashboard");
      return;
    }
    if (initedForRef.current === questId) return;
    initedForRef.current = questId;

    // Reset per-quest state before loading the new quest's messages.
    setMessages([]);
    setCompleted(false);
    setError("");
    setInput("");

    (async () => {
      setBusy(true);
      try {
        const prog = await api.getProgressOne(user.id, questId);
        if (prog.messages && prog.messages.length > 0) {
          setMessages(prog.messages);
          if (prog.status === "completed") setCompleted(true);
        } else {
          const r = await api.chat({ user_id: user.id, quest_id: questId, is_first: true });
          setMessages([{ role: "assistant", content: r.message }]);
        }
      } catch (e) {
        if (e instanceof HttpError) {
          if (e.status === 403) {
            navigate("/paywall");
            return;
          }
          setError(typeof e.body === "object" ? JSON.stringify(e.body) : "Failed to load quest.");
        } else {
          setError("Network error.");
        }
      } finally {
        setBusy(false);
      }
    })();
  }, [user, quest, questId, navigate]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  async function handleSend(e) {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy || completed) return;
    setInput("");
    setBusy(true);
    setError("");
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    try {
      const r = await api.chat({ user_id: user.id, quest_id: questId, message: text });
      setMessages((prev) => [...prev, { role: "assistant", content: r.message }]);
      if (r.is_complete) {
        setCompleted(true);
        if (questId === 0) {
          const m = r.message.match(PERSONA_REGEX);
          if (m && m[1]) {
            try {
              const updated = await api.patchUser(user.id, { founder_persona: m[1] });
              patchUser(updated);
            } catch (_) {
              // non-blocking
            }
          }
        }
      }
    } catch (e) {
      if (e instanceof HttpError && e.status === 403) {
        navigate("/paywall");
        return;
      }
      setError("The mentor stumbled. Try sending again.");
    } finally {
      setBusy(false);
    }
  }

  function handleKey(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  if (!quest || !user) return null;
  const Icon = ICON_MAP[quest.icon];
  const userMsgCount = messages.filter((m) => m.role === "user").length;
  const progressPct = Math.min(userMsgCount / TARGET_USER_MSGS, 1) * 100;

  return (
    <main data-testid="quest-page" className="min-h-screen flex flex-col">
      {/* Progress bar */}
      <div className="h-1 w-full bg-zinc-900">
        <div
          className="h-full bg-gradient-to-r from-amber-500 to-amber-300 transition-all duration-700"
          style={{ width: `${progressPct}%` }}
          data-testid="quest-progress-bar"
        />
      </div>

      {/* Header */}
      <header className="px-6 sm:px-10 lg:px-20 py-5 border-b border-zinc-900 flex items-center gap-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-300">
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
            {quest.part} <span className="text-zinc-700">·</span> Quest {quest.id + 1}/13
          </p>
          <h1 data-testid="quest-title" className="text-base sm:text-lg font-semibold text-zinc-100 truncate">
            {quest.title}
          </h1>
        </div>
        <button
          data-testid="quest-close-btn"
          onClick={() => navigate("/dashboard")}
          className="rounded-md p-2 text-zinc-500 hover:text-zinc-200 hover:bg-zinc-900"
          aria-label="Back to dashboard"
        >
          <X className="h-5 w-5" />
        </button>
      </header>

      {/* Messages */}
      <div
        ref={scrollRef}
        data-testid="quest-messages"
        className="flex-1 overflow-y-auto px-6 sm:px-10 lg:px-20 py-8"
      >
        <div className="max-w-3xl mx-auto space-y-5">
          {messages.map((m, i) => (
            <div
              key={i}
              data-testid={`msg-${i}-${m.role}`}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 leading-relaxed ${
                  m.role === "user"
                    ? "bg-amber-500/[0.12] border border-amber-500/20 text-amber-50"
                    : "bg-zinc-900/70 border border-zinc-800 text-zinc-200"
                }`}
              >
                <div className="whitespace-pre-wrap text-sm">{m.content}</div>
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="rounded-2xl px-4 py-3 bg-zinc-900/70 border border-zinc-800">
                <TypingDots />
              </div>
            </div>
          )}
          {error && (
            <p className="text-xs text-red-400 text-center" data-testid="quest-error">
              {error}
            </p>
          )}
        </div>
      </div>

      {/* Composer or Complete CTA */}
      <footer className="px-6 sm:px-10 lg:px-20 py-5 border-t border-zinc-900 bg-zinc-950/60 backdrop-blur-md">
        <div className="max-w-3xl mx-auto">
          {completed ? (
            <div data-testid="quest-complete-overlay" className="flex flex-col sm:flex-row items-center gap-3 justify-between">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300">quest complete</p>
                <p className="text-zinc-200">Your founder dimensions have been scored.</p>
              </div>
              <Button
                data-testid="goto-scorecard-btn"
                onClick={() => navigate(`/scorecard/${questId}`)}
                className="h-11 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold gap-2"
              >
                See your scorecard <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSend} className="flex items-end gap-3">
              <textarea
                data-testid="quest-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder={busy ? "Mentor is thinking…" : "Type your answer — Enter to send, Shift+Enter for newline"}
                rows={1}
                disabled={busy}
                aria-busy={busy}
                className={`flex-1 resize-none rounded-xl border border-zinc-800 bg-zinc-950 text-zinc-100 placeholder:text-zinc-600 px-4 py-3 text-sm leading-relaxed max-h-40 focus:outline-none focus:border-amber-500/50 transition-opacity ${
                  busy ? "opacity-60 cursor-not-allowed" : "opacity-100"
                }`}
              />
              <Button
                type="submit"
                data-testid="quest-send-btn"
                disabled={busy || !input.trim()}
                className="h-11 px-4 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold gap-2"
              >
                <Send className="h-4 w-4" />
              </Button>
            </form>
          )}
        </div>
      </footer>
    </main>
  );
}
